import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import type { GridColDef } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import MasterFilter, { type FilterFieldConfig, useMasterFilter } from 'components/common/MasterFilter';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateCarModelMutation,
  useDeleteCarModelMutation,
  useGetCarModelsQuery,
  useUpdateCarModelMutation,
} from 'services/api/carModels.api';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import {
  type CarModel,
  type CarModelDraft,
  type CarType,
  CarTypes,
  getAvailableSubCategories,
} from '../../services/types/carModel';

const CarModels = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CarModelDraft>({
    brand: '',
    name: '',
    allowedNames: [],
    category: null,
    subCategory: null,
  });

  const navigate = useNavigate();
  const { data: carModels, refetch: refetchCarModels } = useGetCarModelsQuery(null);
  const [createCarModel] = useCreateCarModelMutation();
  const [updateCarModel] = useUpdateCarModelMutation();
  const [deleteCarModel] = useDeleteCarModelMutation();

  // MasterFilter bar (single text field)
  const barConfig = useMemo(
    (): FilterFieldConfig[] => [
      {
        key: 'searchTerm',
        kind: 'text',
        label: 'Buscar',
        placement: 'bar',
        placeholder: 'Marca, nombre, categoría…',
      },
    ],
    []
  );
  const masterFilter = useMasterFilter(barConfig, null);
  const rawSearch = (masterFilter.values.searchTerm as string | undefined) ?? '';
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(rawSearch.toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const handleClose = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      brand: '',
      name: '',
      allowedNames: [],
      category: null,
      subCategory: null,
    });
  };

  const isFormValid =
    formData.brand && formData.name && formData.allowedNames.length > 0 && formData.category && formData.subCategory;

  const handleSubmit = () => {
    if (isFormValid) {
      if (editingId) {
        updateCarModel({ id: editingId, body: formData }).catch((error) => {
          console.error('Error saving car model:', error);
        });
      } else {
        createCarModel(formData).catch((error) => {
          console.error('Error saving car model:', error);
        });
      }
      refetchCarModels().catch((error) => {
        console.error('Error refetching car models:', error);
      });
      handleClose();
    }
  };

  const handleEdit = (row: CarModel) => {
    setEditingId(row.id);
    setFormData({
      brand: row.brand,
      name: row.name,
      allowedNames: row.allowedNames,
      category: row.category,
      subCategory: row.subCategory,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este modelo?')) {
      deleteCarModel(id).catch((error) => {
        console.error('Error deleting car model:', error);
      });
      refetchCarModels().catch((error) => {
        console.error('Error refetching car models:', error);
      });
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'brand', headerName: 'Marca', width: 130 },
    { field: 'name', headerName: 'Nombre', width: 130 },
    {
      field: 'allowedNames',
      headerName: 'Nombres Permitidos',
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%', flexWrap: 'wrap' }}>
          {Array.isArray(params.value) &&
            params.value.map((name: string) => <Chip key={name} label={name} size="small" />)}
        </Box>
      ),
    },
    { field: 'category', headerName: 'Categoría', width: 130 },
    { field: 'subCategory', headerName: 'Subcategoría', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Editar modelo">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.row as CarModel);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar modelo">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete((params.row as CarModel).id);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredCarModels = useMemo(() => {
    if (!carModels) return carModels;
    if (!debouncedSearch) return carModels;
    return carModels.filter(
      (model) =>
        model.brand.toLowerCase().includes(debouncedSearch) ||
        model.name.toLowerCase().includes(debouncedSearch) ||
        model.category?.toLowerCase().includes(debouncedSearch) ||
        model.subCategory?.toLowerCase().includes(debouncedSearch)
    );
  }, [carModels, debouncedSearch]);

  const pillBtnSx = {
    height: 32,
    borderRadius: RADIUS_COIN,
    textTransform: 'none' as const,
    fontFamily: 'Jost',
    fontWeight: 500,
    fontSize: '0.75rem',
    color: NAVY,
    borderColor: `${NAVY}1f`,
    px: 2,
    '&:hover': { backgroundColor: NAVY, color: '#fff', borderColor: NAVY },
  } as const;

  const primaryCtaSx = {
    height: 32,
    borderRadius: RADIUS_COIN,
    textTransform: 'none' as const,
    fontFamily: 'Jost',
    fontWeight: 600,
    fontSize: '0.75rem',
    backgroundColor: YELLOW,
    color: NAVY,
    px: 2,
    boxShadow: 'none',
    '&:hover': { backgroundColor: `${YELLOW}cc`, boxShadow: 'none' },
  } as const;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: SURFACE }}>
      <Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
        <MasterFilter {...masterFilter} />
      </Box>

      {/* Actions row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          pt: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
            sx={primaryCtaSx}
          >
            Agregar Modelo
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate('/cars')} sx={pillBtnSx}>
            Ver Autos
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          m: 2,
          mt: 1.5,
          p: 2,
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: 3,
          boxShadow: SHADOW,
        }}
      >
        {!carModels && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
            <Skeleton variant="rectangular" height={40} />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Box>
        )}
        {filteredCarModels && (
          <CustomDataGrid<CarModel>
            rows={filteredCarModels}
            columns={columns}
            filterModel={{ items: [] }}
            sortModel={[]}
            onFilterModelChange={() => {}}
            onSortModelChange={() => {}}
            paginationModel={{ page: 0, pageSize: 100 }}
            onPaginationModelChange={() => {}}
            rowCount={filteredCarModels.length}
            getRowClassName={() => ''}
            pageId="carModels"
            disableFilters
            disablePagination
          />
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? 'Editar Modelo' : 'Nuevo Modelo'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Marca"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
            />
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Nombres permitidos"
              value={formData.allowedNames.join(', ')}
              onChange={(e) =>
                setFormData({ ...formData, allowedNames: e.target.value.split(',').map((s) => s.trim()) })
              }
              helperText="Separar con comas"
            />
            <FormControl>
              <InputLabel id="select-category">Categoría</InputLabel>
              <Select
                id="select-category"
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as CarType, subCategory: null })}
                label="Categoría"
              >
                {CarTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.category && (
              <FormControl>
                <InputLabel id="select-subcategory">Subcategoría</InputLabel>
                <Select
                  label="Subcategoría"
                  value={formData.subCategory}
                  onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                >
                  {getAvailableSubCategories(formData.category)?.map((subCategory) => (
                    <MenuItem key={subCategory} value={subCategory}>
                      {subCategory}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!isFormValid}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarModels;
